import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import {
  useCreatePurchaseOrderMutation,
  useGetPaymentAccountsQuery,
  useGetSuppliersQuery,
  useGetPurchaseOrderQuery,
} from '../../../store/api/purchaseApi';
import { useLazyGetProductsQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { SupplierFormContent } from './SupplierForm';

function PurchaseOrderForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const supplierIdFromUrl = searchParams.get('supplierId') || '';
  const isLpoFromUrl = searchParams.get('lpo') === '1';
  const isView = !!id;
  const [createPurchaseOrder] = useCreatePurchaseOrderMutation();
  const { data: suppliers } = useGetSuppliersQuery();
  const { data: paymentAccounts } = useGetPaymentAccountsQuery();
  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] =
    useLazyGetProductsQuery();
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const { data: existingOrder, isLoading: loadingOrder } = useGetPurchaseOrderQuery(id, {
    skip: !isView,
  });

  const [formData, setFormData] = useState({
    invoiceNumber: '',
    lpoNumber: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    purchaseType: 'credit',
    postingType: 'inventory',
    supplierId: '',
    orderDate: new Date().toISOString().split('T')[0],
    notes: '',
    discountTotal: 0,
    roundOff: 0,
    paidAmount: 0,
    paymentMethod: '',
    paymentReferenceNo: '',
    paymentAccountId: '',
  });

  const [items, setItems] = useState([]);
  const [productSearch, setProductSearch] = useState('');

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleAddProductRow = (product) => {
    if (!product) return;
    setItems((prev) => [
      ...prev,
      {
        productId: product.id,
        productName: product.name,
        productSku: product.sku,
        quantity: 1,
        unitPrice: product.costPrice || 0,
      },
    ]);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => {
      return sum + parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0);
    }, 0);
  };

  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const vatRate = toNum(company?.vatRate || 0) / 100;
  const vatIncluded = company?.purchaseVatIncluded !== false;
  const subtotal = toNum(calculateTotal());
  const discountTotal = Math.max(0, Math.min(toNum(formData.discountTotal), subtotal));
  const taxable = Math.max(0, subtotal - discountTotal);
  const vatTotal =
    vatRate > 0
      ? vatIncluded
        ? taxable - taxable / (1 + vatRate)
        : taxable * vatRate
      : 0;
  const grandTotal = vatRate > 0 ? (vatIncluded ? taxable : taxable + vatTotal) : taxable;
  const roundOff = toNum(formData.roundOff);
  const totalAmount = grandTotal + roundOff;
  const paidAmount =
    formData.purchaseType === 'cash'
      ? totalAmount
      : Math.max(0, toNum(formData.paidAmount));
  const balanceAmount = totalAmount - paidAmount;

  useEffect(() => {
    if (!isView && supplierIdFromUrl) {
      setFormData((prev) => ({ ...prev, supplierId: supplierIdFromUrl }));
    }
  }, [isView, supplierIdFromUrl]);

  useEffect(() => {
    if (!isView && isLpoFromUrl && !formData.lpoNumber) {
      const suggested = `LPO-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Date.now().toString(36).toUpperCase().slice(-4)}`;
      setFormData((prev) => ({ ...prev, lpoNumber: suggested }));
    }
  }, [isView, isLpoFromUrl]);

  useEffect(() => {
    if (existingOrder && isView) {
      setFormData((prev) => ({
        ...prev,
        invoiceNumber: existingOrder.invoiceNumber || '',
        lpoNumber: existingOrder.lpoNumber || '',
        invoiceDate: existingOrder.invoiceDate
          ? existingOrder.invoiceDate.substring(0, 10)
          : prev.invoiceDate,
        purchaseType: existingOrder.purchaseType || prev.purchaseType,
        postingType: existingOrder.postingType || prev.postingType,
        supplierId: existingOrder.supplierId || '',
        orderDate: existingOrder.orderDate
          ? existingOrder.orderDate.substring(0, 10)
          : prev.orderDate,
        notes: existingOrder.notes || '',
        discountTotal: existingOrder.discountTotal ?? 0,
        roundOff: existingOrder.roundOff ?? 0,
        paidAmount: existingOrder.paidAmount ?? 0,
        paymentMethod: existingOrder.paymentMethod || '',
        paymentReferenceNo: existingOrder.paymentReferenceNo || '',
        paymentAccountId: existingOrder.paymentAccountId || '',
      }));
      setItems(
        (existingOrder.items || []).map((it) => ({
          productId: it.productId,
          quantity: it.quantity,
          unitPrice: it.unitPrice,
          productName: it.product?.name,
          productSku: it.product?.sku,
        }))
      );
    }
  }, [existingOrder, isView]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isView) {
      navigate('/purchases');
      return;
    }
    if (!formData.invoiceNumber || !formData.invoiceNumber.trim()) {
      alert('Invoice Number is required');
      return;
    }
    if (!formData.invoiceDate) {
      alert('Invoice Date is required');
      return;
    }
    if (!formData.supplierId) {
      alert('Supplier is required');
      return;
    }
    if (!items.length) {
      alert('Please add at least one item');
      return;
    }
    if (items.some((i) => !i.productId)) {
      alert('Please select a product for all items');
      return;
    }
    if (paidAmount > 0 && !formData.paymentMethod) {
      alert('Payment Method is required when Paid Amount > 0');
      return;
    }
    try {
      await createPurchaseOrder({
        ...formData,
        invoiceNumber: formData.invoiceNumber.trim(),
        lpoNumber: formData.lpoNumber?.trim() || undefined,
        invoiceDate: formData.invoiceDate,
        purchaseType: formData.purchaseType,
        postingType: formData.postingType,
        discountTotal: toNum(formData.discountTotal),
        roundOff: toNum(formData.roundOff),
        paidAmount,
        paymentMethod: paidAmount > 0 ? formData.paymentMethod : undefined,
        paymentReferenceNo: paidAmount > 0 ? (formData.paymentReferenceNo || undefined) : undefined,
        paymentAccountId: paidAmount > 0 ? (formData.paymentAccountId || undefined) : undefined,
        items: items.map((item) => ({
          productId: item.productId,
          quantity: parseFloat(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
        })),
      }).unwrap();
      navigate('/purchases');
    } catch (error) {
      alert(error?.data?.message || 'Error creating purchase order');
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        {isView ? 'Purchase Order Details' : 'New Purchase Order'}
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Invoice Number"
                name="invoiceNumber"
                value={formData.invoiceNumber}
                onChange={(e) => setFormData({ ...formData, invoiceNumber: e.target.value })}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label="Invoice Date"
                name="invoiceDate"
                value={formData.invoiceDate}
                onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="LPO Reference No."
                name="lpoNumber"
                value={formData.lpoNumber}
                onChange={(e) => setFormData({ ...formData, lpoNumber: e.target.value })}
                placeholder="e.g. LPO-20240301-001"
                helperText={!isView ? 'Local Purchase Order reference (optional)' : ''}
                disabled={isView}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Purchase Type"
                name="purchaseType"
                value={formData.purchaseType}
                onChange={(e) => {
                  const next = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    purchaseType: next,
                    paidAmount: next === 'cash' ? totalAmount : prev.paidAmount,
                    paymentMethod: next === 'cash' ? (prev.paymentMethod || 'cash') : prev.paymentMethod,
                  }));
                }}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Posting Type"
                name="postingType"
                value={formData.postingType}
                onChange={(e) => setFormData({ ...formData, postingType: e.target.value })}
              >
                <MenuItem value="inventory">Inventory</MenuItem>
                <MenuItem value="expense">Expense</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  select
                  label="Supplier"
                  name="supplierId"
                  value={formData.supplierId}
                  onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}
                  required
                >
                  <MenuItem value="">Select Supplier</MenuItem>
                  {suppliers?.map((supplier) => (
                    <MenuItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="outlined"
                  onClick={() => setSupplierDialogOpen(true)}
                  sx={{ height: 56, minWidth: 44 }}
                  title="Add Supplier"
                >
                  <PersonAddAltIcon fontSize="small" />
                </Button>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Order Date"
                name="orderDate"
                value={formData.orderDate}
                onChange={(e) => setFormData({ ...formData, orderDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Order Items
                </Typography>
                {!isView && (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      alignItems: 'center',
                      minWidth: { xs: '100%', sm: 420 },
                      maxWidth: 600,
                    }}
                  >
                    <Autocomplete
                      fullWidth
                      size="medium"
                      autoHighlight
                      options={productSearchData?.products || []}
                      loading={searchingProducts}
                      value={null}
                      inputValue={productSearch}
                      getOptionLabel={(option) =>
                        option?.name ? `${option.name} (${option.sku || option.barcode || ''})` : ''
                      }
                      onInputChange={(_, value) => {
                        setProductSearch(value);
                        if (value && value.length >= 2) {
                          triggerProductSearch({ search: value, limit: 20 });
                        }
                      }}
                      onChange={(_, value) => {
                        if (value) {
                          handleAddProductRow(value);
                          setProductSearch('');
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Scan or search item (name / barcode)"
                          placeholder="Type at least 2 characters"
                          sx={{
                            '& .MuiInputBase-root': {
                              height: 52,
                              borderRadius: 2,
                            },
                          }}
                        />
                      )}
                    />
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      sx={{ whiteSpace: 'nowrap' }}
                      onClick={() => {
                        if (productSearch && productSearch.length >= 2) {
                          triggerProductSearch({ search: productSearch, limit: 20 });
                        }
                      }}
                    >
                      Search
                    </Button>
                  </Box>
                )}
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{`Unit Price (${currency})`}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{`Total (${currency})`}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => {
                      const total = parseFloat(item.quantity || 0) * parseFloat(item.unitPrice || 0);
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.9rem', fontWeight: 500 }}>
                              {item.productName || 'Product'}
                              {item.productSku ? ` (${item.productSku})` : ''}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                              sx={{ width: 100 }}
                              inputProps={{ min: 0.01, step: 0.01 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={item.unitPrice}
                              onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                              sx={{ width: 120 }}
                              inputProps={{ min: 0, step: 0.01 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{total.toFixed(2)}</TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveItem(index)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {items.length > 0 && (
                <Box sx={{ mt: 2, textAlign: 'right' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {`Total (${currency}): ${calculateTotal().toFixed(2)}`}
                  </Typography>
                </Box>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
                Purchase Summary (Auto Calculated)
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={`Subtotal (${currency})`}
                value={subtotal.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={`Discount Total (${currency})`}
                name="discountTotal"
                value={formData.discountTotal}
                onChange={(e) => setFormData({ ...formData, discountTotal: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={`VAT Total (${currency})`}
                value={vatTotal.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={`Grand Total (${currency})`}
                value={grandTotal.toFixed(2)}
                InputProps={{ readOnly: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={`Round Off (${currency})`}
                name="roundOff"
                value={formData.roundOff}
                onChange={(e) => setFormData({ ...formData, roundOff: e.target.value })}
                inputProps={{ step: 0.01 }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={`Total (${currency})`}
                value={totalAmount.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="number"
                label={`Paid Amount (${currency})`}
                name="paidAmount"
                value={formData.purchaseType === 'cash' ? totalAmount.toFixed(2) : formData.paidAmount}
                onChange={(e) => setFormData({ ...formData, paidAmount: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
                disabled={formData.purchaseType === 'cash'}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label={`Balance Amount (${currency})`}
                value={balanceAmount.toFixed(2)}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mt: 1 }}>
                Payment Section
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Payment Method"
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                disabled={paidAmount <= 0}
                required={paidAmount > 0}
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="bank">Bank</MenuItem>
                <MenuItem value="card">Card</MenuItem>
                <MenuItem value="cheque">Cheque</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Payment Reference No"
                name="paymentReferenceNo"
                value={formData.paymentReferenceNo}
                onChange={(e) => setFormData({ ...formData, paymentReferenceNo: e.target.value })}
                disabled={paidAmount <= 0}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                select
                label="Payment Account (Cash/Bank Ledger)"
                name="paymentAccountId"
                value={formData.paymentAccountId}
                onChange={(e) => setFormData({ ...formData, paymentAccountId: e.target.value })}
                disabled={paidAmount <= 0}
              >
                <MenuItem value="">Auto (by method)</MenuItem>
                {paymentAccounts?.map((acc) => (
                  <MenuItem key={acc.id} value={acc.id}>
                    {acc.code} - {acc.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type={isView ? 'button' : 'submit'}
                  variant="contained"
                  disabled={!isView && items.length === 0}
                  onClick={isView ? () => navigate('/purchases') : undefined}
                >
                  {isView ? 'Back to Purchases' : 'Create Order'}
                </Button>
                {!isView && (
                  <Button variant="outlined" onClick={() => navigate('/purchases')}>
                    Cancel
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Dialog
        open={supplierDialogOpen}
        onClose={() => setSupplierDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1.5, boxShadow: '0 12px 40px rgba(0,0,0,0.15)' } }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Add Supplier</DialogTitle>
        <DialogContent
          sx={{
            pt: 2,
            overflow: 'visible',
            '& .MuiInputLabel-root': { overflow: 'visible' },
          }}
        >
          <SupplierFormContent
            compact
            onCancel={() => setSupplierDialogOpen(false)}
            onSuccess={(created) => {
              setSupplierDialogOpen(false);
              if (created?.id) {
                setFormData((prev) => ({ ...prev, supplierId: created.id }));
              }
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default PurchaseOrderForm;
